import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DollarSign, Percent, Calendar, TrendingUp } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const THEME_PRIMARY = 'rgb(8, 120, 191)';
const THEME_ACCENT = 'rgb(46, 184, 230)';
const THEME_LIGHT = 'rgb(219, 239, 252)';

const MortgageCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  useEffect(() => {
    calculateMortgage();
  }, [loanAmount, interestRate, loanTerm, paymentFrequency]);

  const calculateMortgage = () => {
    const principal = parseFloat(loanAmount) || 0;
    const annualRate = parseFloat(interestRate) / 100 || 0;
    const years = parseFloat(loanTerm) || 0;

    if (principal <= 0 || annualRate <= 0 || years <= 0) {
      setMonthlyPayment(0);
      setTotalPayment(0);
      setTotalInterest(0);
      return;
    }

    // Calculate based on frequency
    let paymentsPerYear, ratePerPeriod, totalPayments;
    
    switch(paymentFrequency) {
      case 'weekly':
        paymentsPerYear = 52;
        break;
      case 'fortnightly':
        paymentsPerYear = 26;
        break;
      case 'monthly':
      default:
        paymentsPerYear = 12;
        break;
    }

    ratePerPeriod = annualRate / paymentsPerYear;
    totalPayments = years * paymentsPerYear;

    // Calculate payment using mortgage formula
    const payment = principal * 
      (ratePerPeriod * Math.pow(1 + ratePerPeriod, totalPayments)) / 
      (Math.pow(1 + ratePerPeriod, totalPayments) - 1);

    const total = payment * totalPayments;
    const interest = total - principal;

    setMonthlyPayment(payment);
    setTotalPayment(total);
    setTotalInterest(interest);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPaymentLabel = () => {
    switch(paymentFrequency) {
      case 'weekly': return 'Weekly Payment';
      case 'fortnightly': return 'Fortnightly Payment';
      case 'monthly':
      default: return 'Monthly Payment';
    }
  };

  // Compute amortization data points at yearly intervals
  const chartData = useMemo(() => {
    const principal = parseFloat(loanAmount) || 0;
    const annualRate = parseFloat(interestRate) / 100 || 0;
    const years = parseInt(loanTerm) || 0;

    if (principal <= 0 || annualRate <= 0 || years <= 0) return [];

    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;
    const pmt =
      principal *
      (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1);

    // Determine tick interval: every 5 years, but always include year 0 and last year
    const interval = 1;
    const points = [{ year: 0, balance: principal, totalPaid: 0, interestPaid: 0 }];

    let balance = principal;
    let cumulativePaid = 0;
    let cumulativeInterest = 0;

    for (let m = 1; m <= totalMonths; m++) {
      const interestThisMonth = balance * monthlyRate;
      const principalThisMonth = pmt - interestThisMonth;
      balance = Math.max(0, balance - principalThisMonth);
      cumulativePaid += pmt;
      cumulativeInterest += interestThisMonth;

      const currentYear = m / 12;
      // Capture at every 5-year mark, or the final month
      if (m % (interval * 12) === 0 || m === totalMonths) {
        points.push({
          year: Math.round(currentYear),
          balance: Math.round(balance),
          totalPaid: Math.round(cumulativePaid),
          interestPaid: Math.round(cumulativeInterest),
        });
      }
    }

    return points;
  }, [loanAmount, interestRate, loanTerm]);

  const formatCurrencyShort = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val}`;
  };

  const ChartTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm">
        <p className="font-semibold text-gray-800 mb-1">Year {label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} style={{ color: entry.color }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <Card className="border-2 shadow-lg">
      <CardHeader className="bg-gradient-to-br from-blue-50 to-white">
        <CardTitle className="text-2xl" style={{ color: 'rgb(8, 120, 191)' }}>
          Mortgage Calculator
        </CardTitle>
        <CardDescription className="text-base">
          Calculate your estimated mortgage repayments
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Loan Amount */}
          <div>
            <Label htmlFor="loan-amount" className="text-base font-semibold flex items-center mb-2">
              <DollarSign className="h-4 w-4 mr-2" style={{ color: 'rgb(8, 120, 191)' }} />
              Loan Amount
            </Label>
            <Input
              id="loan-amount"
              type="number"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              className="text-lg"
            />
            <p className="text-sm text-gray-500 mt-1">{formatCurrency(loanAmount)}</p>
          </div>

          {/* Interest Rate */}
          <div>
            <Label htmlFor="interest-rate" className="text-base font-semibold flex items-center mb-2">
              <Percent className="h-4 w-4 mr-2" style={{ color: 'rgb(8, 120, 191)' }} />
              Annual Interest Rate (%)
            </Label>
            <Input
              id="interest-rate"
              type="number"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="text-lg"
            />
          </div>

          {/* Loan Term */}
          <div>
            <Label htmlFor="loan-term" className="text-base font-semibold flex items-center mb-2">
              <Calendar className="h-4 w-4 mr-2" style={{ color: 'rgb(8, 120, 191)' }} />
              Loan Term (Years)
            </Label>
            <Input
              id="loan-term"
              type="number"
              value={loanTerm}
              onChange={(e) => setLoanTerm(e.target.value)}
              className="text-lg"
            />
          </div>

          {/* Payment Frequency */}
          <div>
            <Label htmlFor="payment-frequency" className="text-base font-semibold flex items-center mb-2">
              <TrendingUp className="h-4 w-4 mr-2" style={{ color: 'rgb(8, 120, 191)' }} />
              Payment Frequency
            </Label>
            <Select value={paymentFrequency} onValueChange={setPaymentFrequency}>
              <SelectTrigger className="text-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results */}
          <div className="mt-8 p-6 rounded-lg" style={{ backgroundColor: THEME_PRIMARY }}>
            <div className="space-y-4 text-white">
              <div className="flex justify-between items-center pb-4 border-b border-blue-400">
                <span className="text-lg font-semibold">{formatPaymentLabel()}</span>
                <span className="text-3xl font-bold">{formatCurrency(monthlyPayment)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-blue-100">Total Amount Payable</span>
                <span className="text-xl font-semibold">{formatCurrency(totalPayment)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-blue-100">Total Interest</span>
                <span className="text-xl font-semibold">{formatCurrency(totalInterest)}</span>
              </div>
            </div>
          </div>

          {/* Loan Balance & Total Paid Chart */}
          {chartData.length > 1 && (
            <div className="mt-8" data-testid="mortgage-chart">
              <h3 className="text-lg font-semibold mb-4" style={{ color: THEME_PRIMARY }}>
                Loan Balance & Total Payments Over Time
              </h3>
              <div className="w-full" style={{ height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradBalance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={THEME_PRIMARY} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={THEME_PRIMARY} stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gradPaid" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={THEME_ACCENT} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={THEME_ACCENT} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="year"
                      tick={{ fill: '#6b7280', fontSize: 13 }}
                      tickLine={false}
                      axisLine={{ stroke: '#d1d5db' }}
                      label={{ value: 'Years', position: 'insideBottomRight', offset: -4, fill: '#6b7280', fontSize: 13 }}
                    />
                    <YAxis
                      tickFormatter={formatCurrencyShort}
                      tick={{ fill: '#6b7280', fontSize: 13 }}
                      tickLine={false}
                      axisLine={{ stroke: '#d1d5db' }}
                      width={62}
                    />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend
                      verticalAlign="top"
                      height={36}
                      iconType="circle"
                      wrapperStyle={{ fontSize: 13 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="balance"
                      name="Loan Balance"
                      stroke={THEME_PRIMARY}
                      strokeWidth={2.5}
                      fill="url(#gradBalance)"
                      dot={{ r: 4, fill: THEME_PRIMARY, strokeWidth: 0 }}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="totalPaid"
                      name="Total Paid"
                      stroke={THEME_ACCENT}
                      strokeWidth={2.5}
                      fill="url(#gradPaid)"
                      dot={{ r: 4, fill: THEME_ACCENT, strokeWidth: 0 }}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center mt-4">
            * This calculator provides estimates only. Actual repayments may vary. Contact us for a detailed assessment.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MortgageCalculator;
